import { createHook, createStore, StoreActionApi } from "react-sweet-state"
import { MemberApplicationList, MemberApplicationListDefaults } from "./entity"
import { MemberApplication } from "../entity";
import { LoadingStateDefaults } from "components/state/loadingstate"
import { load, update } from "components/state/loader"
import { Container } from "components/state/types"
import { EndpointResponse } from "components/http/endpoint"
import { NoEntityError } from "components/validation/validatableentity"
import { EntityState } from "components/state/entity"
import { loadApplicationsForClub, deleteApplication } from "../endpoint"
import { SimpleDataSource } from "components/data/SimpleDataSource";
import { SortModel, FilterModel } from "components/data/DataSource";

// State
enum ContentPage {
    List = 0,
    Detail = 1,
    Edit = 2
}

interface State extends EntityState<MemberApplicationList> {
    clubID: number,
    dataSource: SimpleDataSource<MemberApplication>,
    actions: any,
    page: ContentPage,
    selectedApplicationId?: number;
    needRefresh: boolean;
}
const StateDefaults: State = {
    ...LoadingStateDefaults,
    entity: MemberApplicationListDefaults,
    errors: { ...NoEntityError },
    clubID: null,
    dataSource: null,
    actions: null,
    page: ContentPage.List,
    selectedApplicationId: null,
    needRefresh: false,
}

// Actions

const initDataSource = () =>
    (store: StoreActionApi<State>) => {
        store.setState((draft) => {
            if (draft.dataSource === null) {
                draft.dataSource = new SimpleDataSource();
                draft.dataSource.setData(store.getState().entity.applications);
                draft.dataSource.setPageSize(25, true);

                const sortedItems: { [index: string]: SortModel } = {
                    ['member.surname']: { field: 'member.surname', direction: 'asc' }
                }

                draft.dataSource.setSortedItems(sortedItems);
            }
        });
    };

const setActions = (actions: any) =>
    (store: StoreActionApi<State>) => {
        store.setState((draft) => {
            draft.actions = actions;
            draft.dataSource.setOnSortFunction(actions.loadData);
        });
    };

const loadData = () =>
    (store: StoreActionApi<State>) => {
        if (store.getState().dataSource !== null) {
            let params = store.getState().dataSource.getQueryParameters();

            let loader = () => loadApplicationsForClub(store.getState().clubID, params);

            let stateProducer =
                (draft: State, _: Container, response: EndpointResponse): void => {
                    if (response.succeeded) {

                        draft.entity.applications = [];
                        response.data.forEach((element: MemberApplication) => {
                            draft.entity.applications.push(element);
                        });
                        draft.dataSource.setData(draft.entity.applications);
                        draft.entity.loaded = true;

                    } else {
                        draft.entity.loaded = true;
                    }
                    draft.needRefresh = false;
                }

            load<State>(store, loader, stateProducer);
        }
    };

const onSearchChanged = (value: string) =>
    (store: StoreActionApi<State>) => {
        store.setState((draft) => {
            if (value != "") {
                const filterItems: { [index: string]: FilterModel } = {
                    ['member.surname']: { field: 'member.surname', operator: 'contains', value: value },
                }
                draft.dataSource.setFilteredItems(filterItems);
            } else {
                draft.dataSource.setFilteredItems({});
            }
        });

        if (store.getState().dataSource !== null) {
            let params = store.getState().dataSource.getQueryParameters();

            let loader = () => loadApplicationsForClub(store.getState().clubID, params);

            let stateProducer =
                (draft: State, _: Container, response: EndpointResponse): void => {
                    if (response.succeeded) {
                        draft.entity.applications = [];
                        response.data.forEach((element: MemberApplication) => {
                            draft.entity.applications.push(element);
                        });
                        draft.dataSource.setData(draft.entity.applications);
                        draft.entity.loaded = true;

                    }
                }

            load<State>(store, loader, stateProducer);
        }
    };

const deleteAppl = (appl: MemberApplication) =>
    (store: StoreActionApi<State>) => {

        let appl_idx: number = null;
        for (let i = 0; i < store.getState().entity.applications.length; i++) {
            if (store.getState().entity.applications[i].id == appl.id)
                appl_idx = i;
        }

        let updater = () =>
            deleteApplication(store.getState().entity.applications[appl_idx]);

        let stateProducer =
            (draft: State, _: Container, response: EndpointResponse): void => {
                if (response.succeeded) {
                    draft.needRefresh = true;
                }
            }

        update<State>(store, updater, stateProducer);
    };

const setClubID = (clubID: number) =>
    (store: StoreActionApi<State>) => {
        store.setState((draft) => {
            draft.clubID = clubID;
        });
    };

const goToPage = (page: ContentPage, applicationId: number = null) =>
    (store: StoreActionApi<State>) => {
        store.setState((draft) => {
            draft.page = page;
            draft.selectedApplicationId = applicationId;
        });
    };

const store = createStore({
    initialState: StateDefaults,
    actions: {
        loadData,
        goToPage,
        initDataSource,
        onSearchChanged,
        deleteAppl,
        setClubID,
        setActions,
    },
    name: "ClubMemberApplicationList"
});

const useState = createHook(store);

export {
    ContentPage,
    StateDefaults as ApplicationStateDefaults,
    State as ApplicationListState,
    useState as useApplicationList
}
